// asset-contacts.ts — shared contact-identity grouping logic, extracted so
// every consumer of "an asset's contacts" (the Contacts tab in
// components/assets/asset-contacts.tsx, and the graph explorer's Contacts
// entity kind in routes/$org/graph.tsx) derives the exact same list from the
// exact same rule, rather than each re-implementing it. See spec
// 2026-07-13-01-refonte-contacts-parties (grouping) and
// 2026-07-17-03-asset-contacts-group-by-contact (CLIENT-role hiding).
//
// Contracts have no dedicated "asset contacts" list endpoint — contacts are
// derived client-side from GET /contracts?asset_uid=X&include=stakeholders,
// grouped by contact identity: a groupement (a party_group user) becomes one
// GroupItem listing its members, a lone contact/company becomes one
// IndividualItem — each contact appears ONCE regardless of how many contracts
// it is a stakeholder of, with every contract/role it holds collected as a
// RoleLine on that one item.

import type { Contract, ContractStakeholder } from '../api/schema'

/** CLIENT stakeholder roles are hidden by default (noise: almost always the
 * owning org itself on every service contract). Compared by `code`, never by
 * display name. */
export const CLIENT_STAKEHOLDER_TYPE_CODE = 'CLIENT'

export type ContactMemberView = { uid?: string; name?: string; sharesPercent?: number | null }

export type ContactRoleLine = {
    contractUid?: string
    contractName?: string
    contractCode?: string
    stakeholderTypeCode?: string
    /** Full multi-language names map for the stakeholder type (e.g.
     * `{ en: "Supplier", fr: "Fournisseur" }`), resolved to the UI locale by
     * the renderer (see `lib/localization.ts` `getLocalizedName`). Carrying
     * the whole map — instead of a single pre-resolved EN string — is what
     * lets the UI show "Fournisseur" in a French locale. */
    stakeholderTypeNames?: { [key: string]: string }
    /** Full multi-language names map for the contract's own type (e.g.
     * `WORK_MAINTENANCE_DOOR` -> "Maintenance porte"), present when the
     * contract was fetched with `include=contract_type`. */
    contractTypeNames?: { [key: string]: string }
    /** The contract type's code. `RELATION` is the lightweight link that
     * carries a role without any contract (spec 2026-10-25-25). */
    contractTypeCode?: string
}

export type IndividualContactItem = {
    kind: 'individual'
    uid?: string
    name?: string
    email?: string
    /** The contact's phone (`properties.phone`), shown when there is no email
     * or next to it (spec 2026-10-25-25). */
    phone?: string
    code?: string | null
    roles: ContactRoleLine[]
}

export type GroupContactItem = {
    kind: 'group'
    groupUid: string
    groupName?: string
    groupKind: string
    members: ContactMemberView[]
    roles: ContactRoleLine[]
}

export type ContactItem = IndividualContactItem | GroupContactItem

// groupContactsByIdentity folds a list of contracts (fetched with
// include=stakeholders,user,stakeholder_type,members) into one ContactItem per
// contact identity, in first-seen order.
export function groupContactsByIdentity(contracts: Contract[]): ContactItem[] {
    const out: ContactItem[] = []
    const byKey = new Map<string, ContactItem>()
    for (const c of contracts) {
        // The contract's own type is embedded when fetched with
        // include=contract_type; not declared on the generated Contract
        // schema type (same "embedded object" pattern as property_definition
        // — see front/CLAUDE.md), hence the local cast.
        const contractType = (c as { contract_type?: { names?: { [key: string]: string }; code?: string } }).contract_type
        for (const s of (c.stakeholders ?? []) as ContractStakeholder[]) {
            const stakeholderTypeNames = s.stakeholder_type?.names
            const stakeholderTypeCode = s.stakeholder_type?.code
            const user = s.user as
                | { uid?: string; name?: string | null; email?: string | null; code?: string | null; type?: string; properties?: { kind?: string; phone?: unknown } }
                | undefined

            const role: ContactRoleLine = {
                contractUid: c.uid,
                contractName: c.name ?? undefined,
                contractCode: c.code,
                stakeholderTypeCode,
                stakeholderTypeNames,
                contractTypeNames: contractType?.names,
                contractTypeCode: contractType?.code,
            }

            if (user?.type === 'party_group') {
                const groupUid = user.uid ?? s.uid ?? ''
                const key = `g-${groupUid}`
                let item = byKey.get(key) as GroupContactItem | undefined
                if (!item) {
                    item = {
                        kind: 'group',
                        groupUid,
                        groupName: user.name ?? undefined,
                        groupKind: user.properties?.kind || 'other',
                        members: ((s as { members?: Array<{ user_uid?: string; user?: { name?: string | null }; shares_percent?: number | null }> }).members ?? []).map((m) => ({
                            uid: m.user_uid,
                            name: m.user?.name ?? undefined,
                            sharesPercent: m.shares_percent ?? null,
                        })),
                        roles: [],
                    }
                    byKey.set(key, item)
                    out.push(item)
                }
                item.roles.push(role)
            } else {
                const key = `i-${user?.uid ?? s.uid ?? ''}`
                let item = byKey.get(key) as IndividualContactItem | undefined
                if (!item) {
                    item = {
                        kind: 'individual',
                        uid: user?.uid,
                        name: user?.name ?? undefined,
                        email: user?.email ?? undefined,
                        phone: typeof user?.properties?.phone === 'string' ? user.properties.phone.trim() || undefined : undefined,
                        code: user?.code ?? null,
                        roles: [],
                    }
                    byKey.set(key, item)
                    out.push(item)
                }
                item.roles.push(role)
            }
        }
    }
    return out
}

// filterClientRoles drops CLIENT-only stakeholder roles (and any item left
// with zero roles as a result) unless showClientRoles is set.
export function filterClientRoles(items: ContactItem[], showClientRoles: boolean): ContactItem[] {
    if (showClientRoles) return items
    return items
        .map((item) => ({ ...item, roles: item.roles.filter((r) => r.stakeholderTypeCode !== CLIENT_STAKEHOLDER_TYPE_CODE) }))
        .filter((item) => item.roles.length > 0) as ContactItem[]
}

// countHiddenClientRoles counts how many CLIENT-only role lines are currently
// filtered out, for the "show N hidden" toggle affordance.
export function countHiddenClientRoles(items: ContactItem[]): number {
    return items.reduce(
        (sum, item) => sum + item.roles.filter((r) => r.stakeholderTypeCode === CLIENT_STAKEHOLDER_TYPE_CODE).length,
        0,
    )
}

// uniformStakeholderTypeNames returns the shared stakeholder-type names map
// when every *visible* role line on a card holds the same stakeholder type
// (compared by code, never by display name), so the caller can render the
// role once as a suffix after the contact/group name ("BOUVIER Securite
// Incendie (Fournisseur)") instead of repeating a badge on every line.
// Returns undefined when there are no roles, or the roles hold mixed types —
// callers should then keep a per-line role label instead.
export function uniformStakeholderTypeNames(roles: ContactRoleLine[]): { [key: string]: string } | undefined {
    if (roles.length === 0) return undefined
    const firstCode = roles[0].stakeholderTypeCode
    if (firstCode === undefined) return undefined
    if (!roles.every((r) => r.stakeholderTypeCode === firstCode)) return undefined
    return roles[0].stakeholderTypeNames
}
